import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierPickupRateBand,
  type CarrierProviderAddOnService,
  useAtomicSaveCarrierPickupRateBands,
  useAvailableServiceAreas,
  useCarrierDispatchCenters,
  useCarrierPickupRateBands,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PickupRateLanes,
  type PickupRateAreaRow,
  type PickupRateBracket,
  type PickupRateCell,
  pickupRateBracketKey,
} from "@/features/pickup-rate-lanes";
import { PickupDispatchGuidance } from "./pickup-dispatch-guidance";
import { hasHttpStatus } from "@/lib/api-error";

const defaultBrackets: PickupRateBracket[] = [
  { weightFromKg: 0, weightToKg: 7.14 },
  { weightFromKg: 7.14, weightToKg: 27 },
];

const bracketKey = pickupRateBracketKey;

const dollars = (cents: number | null | undefined) =>
  cents == null ? "" : (cents / 100).toFixed(2);

function buildEditor(
  bands: CarrierPickupRateBand[],
  areaNames: Map<string, string>
) {
  const bracketsByKey = new Map<string, PickupRateBracket>();
  for (const band of bands) {
    const bracket = {
      weightFromKg: Number(band.weightFromKg),
      weightToKg: Number(band.weightToKg),
    };
    bracketsByKey.set(bracketKey(bracket), bracket);
  }
  const brackets =
    bracketsByKey.size > 0
      ? [...bracketsByKey.values()].sort(
          (a, b) => a.weightFromKg - b.weightFromKg
        )
      : defaultBrackets;

  const areaIds = [...new Set(bands.map((band) => band.fromServiceAreaId))];
  const rows = areaIds.map<PickupRateAreaRow>((areaId) => {
    const areaBands = bands.filter((band) => band.fromServiceAreaId === areaId);
    const cells: Record<string, PickupRateCell> = {};
    for (const bracket of brackets) {
      const band = areaBands.find(
        (candidate) =>
          Number(candidate.weightFromKg) === bracket.weightFromKg &&
          Number(candidate.weightToKg) === bracket.weightToKg
      );
      cells[bracketKey(bracket)] = {
        base: dollars(band?.baseRateCents),
        perKg: dollars(band?.perKgCents),
      };
    }
    return {
      areaId,
      name:
        areaBands[0]?.fromServiceArea?.name ?? areaNames.get(areaId) ?? areaId,
      cells,
    };
  });
  return { brackets, rows };
}

export function PickupServiceRates({
  service,
  onDirtyChange = NOOP,
}: {
  service: CarrierProviderAddOnService;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const dispatches = useCarrierDispatchCenters();
  const areas = useAvailableServiceAreas();
  const dispatchOptions = useMemo(
    () =>
      (dispatches.data ?? []).filter(
        (dispatch) =>
          dispatch.pickupProviderCarrierId === service.providerCarrierId
      ),
    [dispatches.data, service.providerCarrierId]
  );
  const [dispatchCenterId, setDispatchCenterId] = useState("");

  useEffect(() => {
    if (!dispatchOptions.some((dispatch) => dispatch.id === dispatchCenterId)) {
      setDispatchCenterId(dispatchOptions[0]?.id ?? "");
    }
  }, [dispatchCenterId, dispatchOptions]);

  const bands = useCarrierPickupRateBands(service.id, dispatchCenterId);
  const areaNames = useMemo(
    () =>
      new Map((areas.data ?? []).map((area) => [area.id, area.name] as const)),
    [areas.data]
  );
  const [brackets, setBrackets] =
    useState<PickupRateBracket[]>(defaultBrackets);
  const [rows, setRows] = useState<PickupRateAreaRow[]>([]);
  const [initialAreaIds, setInitialAreaIds] = useState<string[]>([]);
  const [areaToAdd, setAreaToAdd] = useState("");
  const [addingBracket, setAddingBracket] = useState(false);
  const [newBracketFrom, setNewBracketFrom] = useState("");
  const [newBracketTo, setNewBracketTo] = useState("");
  const [baseline, setBaseline] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useAtomicSaveCarrierPickupRateBands();
  const hydratedScopeRef = useRef("");
  const fingerprint = editorFingerprint(brackets, rows);
  const dirty = baseline !== "" && fingerprint !== baseline;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dispatchCenterId || bands.isLoading) return;
    const scopeKey = `${service.id}:${dispatchCenterId}`;
    if (hydratedScopeRef.current === scopeKey && dirtyRef.current) return;
    const loaded = bands.data ?? [];
    const editor = buildEditor(loaded, areaNames);
    setBrackets(editor.brackets);
    setRows(editor.rows);
    setBaseline(editorFingerprint(editor.brackets, editor.rows));
    setError(null);
    setInitialAreaIds([
      ...new Set(loaded.map((band) => band.fromServiceAreaId)),
    ]);
    hydratedScopeRef.current = scopeKey;
  }, [areaNames, bands.data, bands.isLoading, dispatchCenterId, service.id]);

  const bandsForbidden = hasHttpStatus(bands.error, 403);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const selectableAreas = (areas.data ?? []).filter(
    (area) =>
      area.assignment?.active && !rows.some((row) => row.areaId === area.id)
  );

  const addArea = () => {
    const area = (areas.data ?? []).find(
      (candidate) => candidate.id === areaToAdd
    );
    if (!area) return;
    const cells = Object.fromEntries(
      brackets.map((bracket) => [bracketKey(bracket), { base: "", perKg: "" }])
    );
    setRows((current) => [
      ...current,
      { areaId: area.id, name: area.name, cells },
    ]);
    setAreaToAdd("");
  };

  const updateCell = (
    areaId: string,
    bracket: PickupRateBracket,
    field: keyof PickupRateCell,
    value: string
  ) => {
    const key = bracketKey(bracket);
    setRows((current) =>
      current.map((row) =>
        row.areaId === areaId
          ? {
              ...row,
              cells: {
                ...row.cells,
                [key]: { ...row.cells[key], [field]: value },
              },
            }
          : row
      )
    );
  };

  const addBracket = () => {
    setError(null);
    const weightFromKg = Number(newBracketFrom);
    const weightToKg = Number(newBracketTo);
    if (
      !Number.isFinite(weightFromKg) ||
      !Number.isFinite(weightToKg) ||
      weightFromKg < 0 ||
      weightToKg <= weightFromKg
    ) {
      setError("Enter a valid increasing weight range.");
      return;
    }
    const bracket = { weightFromKg, weightToKg };
    if (brackets.some((item) => bracketKey(item) === bracketKey(bracket))) {
      setError("That weight bracket already exists.");
      return;
    }
    if (
      brackets.some(
        (item) =>
          bracket.weightFromKg < item.weightToKg &&
          bracket.weightToKg > item.weightFromKg
      )
    ) {
      setError("Package billable-weight bands cannot overlap.");
      return;
    }
    setBrackets((current) =>
      [...current, bracket].sort((a, b) => a.weightFromKg - b.weightFromKg)
    );
    setRows((current) =>
      current.map((row) => ({
        ...row,
        cells: {
          ...row.cells,
          [bracketKey(bracket)]: { base: "", perKg: "" },
        },
      }))
    );
    setNewBracketFrom("");
    setNewBracketTo("");
    setAddingBracket(false);
  };

  const removeBracket = (bracket: PickupRateBracket) => {
    const key = bracketKey(bracket);
    setBrackets((current) =>
      current.filter((item) => bracketKey(item) !== key)
    );
    setRows((current) =>
      current.map((row) => {
        const cells = { ...row.cells };
        delete cells[key];
        return { ...row, cells };
      })
    );
  };

  const handleSave = async () => {
    setError(null);
    if (!dispatchCenterId) return;
    const fromServiceAreaIds = [
      ...new Set([...initialAreaIds, ...rows.map((row) => row.areaId)]),
    ];
    if (fromServiceAreaIds.length === 0) {
      setError("Add a sender service area before saving rates.");
      return;
    }

    const payloadBands = [];
    for (const row of rows) {
      for (const bracket of brackets) {
        const cell = row.cells[bracketKey(bracket)] ?? { base: "", perKg: "" };
        if (!cell.base.trim() && !cell.perKg.trim()) continue;
        const base = Number(cell.base);
        const perKg = cell.perKg.trim() ? Number(cell.perKg) : null;
        if (
          !cell.base.trim() ||
          !Number.isFinite(base) ||
          base < 0 ||
          (perKg !== null && (!Number.isFinite(perKg) || perKg < 0))
        ) {
          setError(
            `Enter valid non-negative rates for ${row.name}, ${bracket.weightFromKg}–${bracket.weightToKg} kg.`
          );
          return;
        }
        payloadBands.push({
          fromServiceAreaId: row.areaId,
          dispatchCenterId,
          weightFromKg: bracket.weightFromKg,
          weightToKg: bracket.weightToKg,
          baseRateCents: Math.round(base * 100),
          perKgCents: perKg == null ? null : Math.round(perKg * 100),
          currencyCode: "CAD",
        });
      }
    }

    // The rows this editor was hydrated from, narrowed to the slice the save
    // declares. Without it the Server refuses the save (409) — and before it
    // required one, a second dispatcher's save silently reverted the first.
    const basis = (bands.data ?? [])
      .filter((band) => fromServiceAreaIds.includes(band.fromServiceAreaId))
      .map((band) => ({ id: band.id, updatedAt: band.updatedAt }));

    try {
      await save.mutateAsync({
        carrierAddOnServiceId: service.id,
        dispatchCenterId,
        fromServiceAreaIds,
        bands: payloadBands,
        basis,
      });
      toast.success("Pickup rates saved.");
      setBaseline(fingerprint);
      onDirtyChange(false);
    } catch (error) {
      const response = error as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(error, 403)
        ? "You do not have permission to update pickup rates."
        : (response.response?.data?.error ??
          "Pickup rates could not be saved.");
      setError(message);
      toast.error(message);
    }
  };

  const changeDispatchCenter = (nextId: string) => {
    if (
      dirty &&
      !window.confirm("Discard unsaved rates for this dispatch center?")
    ) {
      return;
    }
    setBaseline("");
    onDirtyChange(false);
    setDispatchCenterId(nextId);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Pickup rate scope</CardTitle>
          <p className="text-sm text-muted-foreground">
            Price sender service area → dispatch center lanes explicitly. A
            blank cell means that pickup option is unavailable; $0 is an
            explicit zero-dollar rate.
          </p>
        </CardHeader>
        <CardContent>
          {dispatches.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading dispatch centers…
            </p>
          ) : dispatches.error ? (
            <PickupDispatchGuidance query={dispatches} />
          ) : (
            <label
              htmlFor="pickup-rate-dispatch"
              className="space-y-1 text-sm font-medium"
            >
              <span>Dispatch center</span>
              <Select
                value={dispatchCenterId}
                onValueChange={changeDispatchCenter}
              >
                <SelectTrigger
                  id="pickup-rate-dispatch"
                  aria-label="Dispatch center"
                  size="sm"
                  className="w-full md:w-80"
                >
                  <SelectValue placeholder="Select dispatch center" />
                </SelectTrigger>
                <SelectContent>
                  {dispatchOptions.map((dispatch) => (
                    <SelectItem key={dispatch.id} value={dispatch.id}>
                      {dispatch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}
        </CardContent>
      </Card>

      {!dispatches.isLoading && !dispatches.error && !dispatchOptions.length ? (
        <PickupDispatchGuidance query={dispatches} />
      ) : dispatchCenterId ? (
        <Card>
          <CardHeader className="flex-col items-stretch justify-between gap-3 space-y-0 sm:flex-row sm:items-start">
            <div>
              <CardTitle>Explicit pickup lanes</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Each row is one sender area. Remove a row to stop offering this
                service from that area.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                variant="outline"
                size="sm-touch"
                className="w-full sm:w-auto"
                onClick={() => {
                  setError(null);
                  setAddingBracket(true);
                }}
              >
                <Plus className="mr-1 h-4 w-4" /> Weight bracket
              </Button>
              <Button
                size="sm-touch"
                className="w-full sm:w-auto"
                onClick={handleSave}
                disabled={save.isPending || !dirty}
              >
                <Save className="mr-1 h-4 w-4" />
                {save.isPending ? "Saving…" : "Save rates"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {bands.error && (
              <div
                role="alert"
                className="flex flex-col gap-2 rounded-md border bg-destructive/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  {bandsForbidden
                    ? "You do not have permission to view pickup rates."
                    : "Pickup rates could not be loaded."}
                </span>
                {!bandsForbidden && (
                  <Button
                    variant="outline"
                    size="sm-touch"
                    onClick={() => void bands.refetch()}
                  >
                    Try again
                  </Button>
                )}
              </div>
            )}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {!bandsForbidden && (!bands.error || rows.length > 0) && (
              <PickupRateLanes
                brackets={brackets}
                rows={rows}
                isLoading={bands.isLoading}
                onUpdateCell={updateCell}
                onRemoveBracket={removeBracket}
                onRemoveRow={(areaId) =>
                  setRows((current) =>
                    current.filter((row) => row.areaId !== areaId)
                  )
                }
              />
            )}

            {areas.error && (
              <Alert variant="destructive">
                <AlertTitle>Sender service areas unavailable</AlertTitle>
                <AlertDescription>
                  <p>
                    {hasHttpStatus(areas.error, 403)
                      ? "You do not have permission to view sender service areas. Contact your carrier administrator."
                      : "Sender service areas could not be loaded."}
                  </p>
                  {!hasHttpStatus(areas.error, 403) && (
                    <Button
                      variant="outline"
                      size="sm-touch"
                      disabled={areas.isFetching}
                      onClick={() => void areas.refetch()}
                    >
                      Retry sender areas
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {areas.isSuccess &&
              !(areas.data ?? []).some((area) => area.assignment?.active) && (
                <Alert role="status">
                  <AlertTitle>No active sender coverage.</AlertTitle>
                  <AlertDescription>
                    <p>
                      Declare or enable service-area coverage before adding
                      pickup lanes.
                    </p>
                    <Button asChild variant="outline" size="sm-touch">
                      <a href="/service-areas" target="_blank" rel="noreferrer">
                        Manage coverage
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm-touch"
                      disabled={areas.isFetching}
                      onClick={() => void areas.refetch()}
                    >
                      Refresh sender areas
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            <div className="flex flex-col items-stretch gap-2 rounded-md border border-dashed p-3 sm:flex-row sm:items-end">
              <label
                htmlFor="pickup-rate-area"
                className="min-w-0 flex-1 space-y-1 text-sm font-medium sm:min-w-64"
              >
                <span>Add sender service area</span>
                <Select value={areaToAdd} onValueChange={setAreaToAdd}>
                  <SelectTrigger
                    id="pickup-rate-area"
                    aria-label="Sender service area"
                    size="sm"
                    className="w-full min-w-0 [&_[data-slot=select-value]]:truncate"
                  >
                    <SelectValue placeholder="Select an active service area" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableAreas.map((area) => (
                      <SelectItem key={area.id} value={area.id}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <Button
                variant="outline"
                size="touch"
                className="w-full sm:w-auto"
                onClick={addArea}
                disabled={!areaToAdd || areas.isLoading || !!areas.error}
              >
                <Plus className="mr-1 h-4 w-4" /> Add lane
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={addingBracket}
        onOpenChange={(open) => {
          setAddingBracket(open);
          if (!open) setError(null);
        }}
        title="Add weight bracket"
        presentation="responsive-sheet"
      >
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label
              htmlFor="pickup-bracket-from"
              className="space-y-1 text-sm font-medium"
            >
              <span>Package billable kg from</span>
              <Input
                id="pickup-bracket-from"
                aria-label="Package billable weight from"
                inputMode="decimal"
                value={newBracketFrom}
                onChange={(event) => setNewBracketFrom(event.target.value)}
              />
            </label>
            <label
              htmlFor="pickup-bracket-to"
              className="space-y-1 text-sm font-medium"
            >
              <span>Package billable kg to</span>
              <Input
                id="pickup-bracket-to"
                aria-label="Package billable weight to"
                inputMode="decimal"
                value={newBracketTo}
                onChange={(event) => setNewBracketTo(event.target.value)}
              />
            </label>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              onClick={() => {
                setError(null);
                setAddingBracket(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="touch"
              className="w-full sm:w-auto"
              onClick={addBracket}
            >
              Add bracket
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

const NOOP = () => undefined;

function editorFingerprint(
  brackets: PickupRateBracket[],
  rows: PickupRateAreaRow[]
): string {
  return JSON.stringify({ brackets, rows });
}
